import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import { ApiError } from '../apiClients/BaseApiClient';
import {
  GetMeResponse,
  UpdateUserRequest,
  User,
} from '../apiClients/workspace/types';
import { workspaceApi } from '../apiClients/workspace/workspaceApi';
import i18n from '../i18n';
import { getDefaultIconIdByWeight } from '../lib/userUtils';
import { notificationStore } from './NotificationStore';
import { skillLevelsStore } from './SkillLevelsStore';

export class UserStore {
  @observable user: GetMeResponse | null = null;
  @observable users: Map<string, User> = new Map();
  @observable isLoading = false;
  @observable isLoadingUsers = false;
  @observable error: Error | null = null;
  @observable usersError: Error | null = null;

  constructor() {
    makeObservable(this);
  }

  // ===============================
  // COMPUTED PROPERTIES
  // ===============================

  @computed get isError(): boolean {
    return this.error !== null;
  }

  @computed get hasData(): boolean {
    return this.user !== null;
  }

  @computed get isUsersError(): boolean {
    return this.usersError !== null;
  }

  @computed get hasUsersData(): boolean {
    return this.users.size > 0;
  }

  @computed get userId(): string | null {
    return this.user?.id || null;
  }

  // ===============================
  // ACTIONS
  // ===============================

  async loadUser(): Promise<void> {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.error = null;

    try {
      const response = await workspaceApi.getMe();
      runInAction(() => {
        this.user = response;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error as Error;
      });
      notificationStore.showError(i18n.t('errors.failedToLoadUserData'));
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async loadUsers(): Promise<void> {
    if (this.isLoadingUsers) {
      return;
    }

    this.isLoadingUsers = true;
    this.usersError = null;

    try {
      const response = await workspaceApi.getUsers();
      runInAction(() => {
        this.users.clear();
        response.users.forEach((user) => {
          this.users.set(user.id, user);
        });
      });
    } catch (error) {
      runInAction(() => {
        this.usersError = error as Error;
      });
      notificationStore.showError(i18n.t('errors.failedToLoadUsers'));
    } finally {
      runInAction(() => {
        this.isLoadingUsers = false;
      });
    }
  }

  getUserSkillLevel(userId: string): string | null {
    const user = this.users.get(userId);
    if (!user?.skillLevel) return null;

    return skillLevelsStore.getSkillLevelName(user.skillLevel);
  }

  getUserSkillLevelWeight(userId: string): number | null {
    const user = this.users.get(userId);
    return user?.skillLevel ?? null;
  }

  getUserSkillLevelIconId(userId: string): string | undefined {
    const user = this.users.get(userId);
    if (!user?.skillLevel) return undefined;

    const iconId = skillLevelsStore.getSkillLevelIconId(user.skillLevel);
    return iconId === getDefaultIconIdByWeight(user.skillLevel)
      ? undefined
      : iconId;
  }

  async updateById(userId: string, data: UpdateUserRequest): Promise<void> {
    if (!this.user?.id) {
      throw new Error('No user data available to update');
    }

    this.isLoading = true;
    this.error = null;

    try {
      const updatedUser = await workspaceApi.updateUser(userId, data);
      runInAction(() => {
        this.users.set(userId, updatedUser);
        if (this.user?.id === userId) {
          this.user = updatedUser;
        }
      });
      notificationStore.showSuccess(i18n.t('profile.updateSuccess'));
    } catch (error) {
      runInAction(() => {
        this.error = error as Error;
      });
      const errorMessage =
        error instanceof ApiError
          ? error.errorMessage
          : i18n.t('errors.failedToUpdateProfile');
      notificationStore.showError(errorMessage);
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  @action clear(): void {
    this.user = null;
    this.users.clear();
    this.isLoading = false;
    this.isLoadingUsers = false;
    this.error = null;
    this.usersError = null;
  }

  // ===============================
  // LIFECYCLE METHODS
  // ===============================

  async initialize(): Promise<void> {
    await Promise.all([this.loadUser(), this.loadUsers()]);
  }

  destroy(): void {
    this.clear();
  }
}

// Export singleton instance
export const userStore = new UserStore();
